import { ApolloClient, InMemoryCache, from } from '@apollo/client';
import { ApolloProvider } from '@apollo/client/react';
import { fromPromise } from 'apollo-link';
import { useHistory } from 'react-router-dom';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { createUploadLink } from 'apollo-upload-client';
import { message } from 'antd';
import { GET_AUTH, REFRESH_TOKEN } from './gql/auth';

const apolloUri = process.env.REACT_APP_GRAPHQL_URL;

const getNewToken = (client) => {
  return new Promise(async (resolve, reject) => {
    try {
      const { data } = await client.mutate({
        mutation: REFRESH_TOKEN,
        variables: { init: false },
        update: (cache, { data: { refreshToken } }) => {
          cache.writeQuery({
            query: GET_AUTH,
            data: {
              auth: {
                token: refreshToken.token,
                me: refreshToken.me,
                isLoggedIn: true,
                loading: false,
              },
            },
          });
        },
      });
      localStorage.setItem('token', data.refreshToken.token);
      resolve({ token: data.refreshToken.token });
    } catch (error) {
      console.log({ error });
      reject(error);
    }
  });
};

const Apollo = ({ children }) => {
  const history = useHistory();
  // const { pathname, search, hash } = useLocation();
  const cache = new InMemoryCache({
    typePolicies: {
      Profile: {
        merge: true,
      },
      Department: {
        merge: true,
      },
    },
  });
  cache.writeQuery({
    query: GET_AUTH,
    data: {
      auth: {
        token: '',
        isLoggedIn: false,
        loading: true,
        me: null,
      },
    },
  });

  const errorLink = onError(
    ({ graphQLErrors, networkError, operation, forward, response }) => {
      if (graphQLErrors) {
        for (let err of graphQLErrors) {
          const { message: errorMessage, locations, path, extensions } = err;
          console.log(
            `[GraphQL error]: Message: ${errorMessage}, Location: ${locations}, Path: ${path}, Extensions: ${JSON.stringify(
              extensions
            )}`
          );
          if (extensions.code === 'INTERNAL_SERVER_ERROR') {
            if (operation.variables.ignoreError) {
              break;
            }
            if (errorMessage === 'IgnorableError') {
              response.errors = null;
              break;
            } else if (errorMessage === 'Not Authorised!') {
              // if ignore not authorised error
              if (operation.variables.ignoreNotAuthorised) {
                break;
              }
              message.error('Not Authorised! Please contact PMS Admin');
              break;
            } else if (errorMessage === 'PROJECT_FROZEN') {
              message.error(
                'Cannot operate on completed / withdrawn / terminated project'
              );
              break;
            } else {
              // TODO: 500 error
              message.error('Internal Server Error');
            }
            break;
          }
          if (extensions.code === 'BAD_USER_INPUT') {
            message.error(errorMessage);
            break;
          }
          if (extensions.code === 'UNAUTHENTICATED') {
            console.warn(errorMessage);
            switch (errorMessage) {
              case 'LOGIN FAIL':
                cache.writeQuery({
                  query: GET_AUTH,
                  data: {
                    auth: {
                      token: '',
                      isLoggedIn: false,
                      me: null,
                      loading: false,
                    },
                  },
                });
                break;
              case 'NO ME':
                cache.writeQuery({
                  query: GET_AUTH,
                  data: {
                    auth: {
                      token: '',
                      isLoggedIn: false,
                      me: null,
                      loading: false,
                    },
                  },
                });
                break;
              case 'GO LOGIN':
                client.clearStore();
                localStorage.removeItem('token');
                history.push('/login');
                break;
              case 'SESSION_TIMEOUT':
                client.clearStore();
                localStorage.removeItem('token');
                history.push('/session-timeout');
                break;
              default:
                // refresh token
                return fromPromise(
                  getNewToken(client)
                    .then(({ token }) => {
                      return token;
                    })
                    .catch((error) => {
                      console.log({ error });
                      if (client) {
                        // clearUser({ client });
                      }
                      history.push(`/login`);
                      return;
                    })
                )
                  .filter((value) => {
                    return Boolean(value);
                  })
                  .flatMap((token) => {
                    // retry the request, returning the new observable
                    operation.setContext(({ headers }) => ({
                      headers: {
                        ...headers,
                        'x-auth-token': token,
                      },
                    }));
                    return forward(operation);
                  });
            }
          }
        }
      }

      if (networkError) console.log(`[Network error]: ${networkError}`);
    }
  );

  // const httpLink = new HttpLink({
  //   uri: 'http://localhost:5000/graphql',
  //   credentials: 'include',
  // });

  const httpLink = createUploadLink({
    uri: apolloUri,
    credentials: 'include',
  });

  const authLink = setContext((_, { headers }) => {
    // get the authentication token from local storage if it exists
    const token = localStorage.getItem('token');
    // console.log(token);
    // return the headers to the context so httpLink can read them
    return {
      headers: {
        ...headers,
        'x-auth-token': token ?? '',
      },
    };
  });

  const client = new ApolloClient({
    cache,
    link: from([authLink, errorLink, httpLink]),
  });

  client.onClearStore(() => {
    console.log('clear store');
    cache.writeQuery({
      query: GET_AUTH,
      data: {
        auth: {
          token: '',
          isLoggedIn: false,
          me: null,
          loading: true,
        },
      },
    });
  });

  return (
    <ApolloProvider client={client} children={children}>
      {children}
    </ApolloProvider>
  );
};

export default Apollo;
